<template>
  <v-container fluid>
    <v-row
      class="rounded elevation-3 white pa-4">
      <v-col
        cols="12"
        md="6"
        class="d-flex flex-row justify-space-between align-center">
        <h2>
          จัดการตำแหน่ง (WMS Location)
        </h2>
      </v-col>
      <v-col
        class="text-right"
        cols="12"
        md="6">
        <v-btn
          :disabled="!totalItems || loadingExport"
          class="mr-2"
          color="green darken-3"
          :loading="loadingExport"
          @click="exportAllLocation()">
          EXPORT
          <v-icon class="ml-1">
            mdi-file
          </v-icon>
        </v-btn>
        <v-btn
          :to="{ name: 'StockMappingCreateLocation' }"
          color="secondary">
          NEW LOCATION
          <v-icon class="ml-4">
            mdi-plus
          </v-icon>
        </v-btn>
      </v-col>
      <v-col
        offset-md="9"
        md="3"
        cols="12">
        <search-box
          v-model="search"
          @on-search="onSearch" />
      </v-col>
      <v-col cols="12">
        <v-data-table
          class="elevation-1"
          :headers="headers"
          :items="items"
          :loading="loading"
          :items-per-page="-1"
          hide-default-footer
        >
          <template v-slot:item.mappings="{ item }">
            <div
              v-if="item.mappings && item.mappings.length"
              class="d-flex flex-row flex-wrap mt-2">
              <!-- Show first 5 mappings if more than 5, else show all -->
              <div
                v-for="(mapping) in item.mappings.slice(0, 5)"
                :key="mapping._id"
                class="mr-4 mb-2 text-center"
              >
                <img
                  width="60"
                  height="60"
                  :src="getMappingPhoto(mapping)"
                  alt=""
                  class="rounded"
                />
                <p class="mb-2 mt-2">
                  {{ getProductCode(mapping.name) }}
                </p>
              </div>

              <!-- "+X more" indicator -->
              <div
                v-if="item.mappings.length > 5"
                class="d-flex align-center justify-center mr-4 mb-2"
                style="width: 70px; height: 80px; font-weight: bold; background: #f0f0f0; border-radius: 8px;"
              >
                +{{ item.mappings.length - 5 }}
              </div>
            </div>

            <!-- Case: no mappings -->
            <div
              v-else
              class="text-left text-muted">
              ไม่มีรายการสินค้า
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              color="secondary"
              class="mr-4"
              icon
              @click="printLabel([item])">
              <v-icon class="ml-1">
                mdi-printer
              </v-icon>
            </v-btn>
            <v-btn
              small
              :to="{
                name: 'StockMappingLocationProduct',
                params: {
                  location: item.id
                }
              }"
              color="secondary">
              VIEW
            </v-btn>
          </template>
          <template v-slot:no-data>
            <p class="mb-0">
              ไม่พบข้อมูล
            </p>
          </template>
        </v-data-table>
      </v-col>
      <v-col cols="12">
        <v-pagination
          v-model="query.page"
          color="secondary"
          :total-visible="7"
          :length="totalPages"
          :disabled="loading" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import NoPicture from '@/assets/image/no_picture_available.png'
import StockMappingProvider from '@/resources/StockMappingProvider'
import SearchBox from '@/components/SearchBox.vue'
import { mapActions } from 'vuex'

const stockMappingService = new StockMappingProvider()

export default {
  components: {
    SearchBox
  },
  data () {
    return {
      loading: false,
      noPicture: NoPicture,
      headers: [
        {
          text: 'Location Name',
          value: 'name',
          sortable: false,
          width: 140
        },
        {
          text: 'Products',
          value: 'mappings',
          sortable: false
        },
        { text: 'Actions', align: 'right', value: 'actions', sortable: false }
      ],
      items: [],
      totalPages: 0,
      totalItems: 0,
      query: {
        page: 1,
        limit: 20,
        sortBy: 'name',
        sortOrder: 'asc'
      },
      search: '',
      loadingExport: false
    }
  },
  watch: {
    query: {
      handler () {
        this.updateRouteQuery()
        this.fetchPaginate()
      },
      deep: true
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    ...mapActions({
      setPrintLocations: 'StockMapping/setPrintLocations'
    }),
    onSearch () {
      this.updateRouteQuery()
      this.fetchPaginate(true)
    },
    updateRouteQuery () {
      const newQuery = {
        ...this.$route.query,
        page: this.query.page,
        search: this.search || undefined
      }

      if (JSON.stringify(this.$route.query) !== JSON.stringify(newQuery)) {
        this.$router.replace({ query: newQuery }).catch((err) => {
          if (
            err.name !== 'NavigationDuplicated'
            && !err.message.includes('Avoided redundant navigation')
          ) {
            throw err
          }
        })
      }
    },
    init () {
      if (this.$route.query.search) {
        this.search = this.$route.query.search
      }

      // page เปลี่ยน → watcher ของ query จะ fetch ให้เอง
      if (this.$route.query.page && Number(this.$route.query.page) !== this.query.page) {
        this.query.page = Number(this.$route.query.page)
      } else {
        this.fetchPaginate()
      }
    },
    async fetchPaginate (isClear = false) {
      if (isClear) {
        this.query.page = 1
        this.totalPages = 0
        this.totalItems = 0
      }

      try {
        this.loading = true

        const { data } = await stockMappingService.getLocationMappingPaginate({ ...this.query, search: this.search })
        this.items = data.results
        this.totalPages = data.pages
        this.totalItems = data.total
      } catch (error) {
        console.error('getLocation', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    printLabel (results) {
      this.setPrintLocations(results)
      this.$router.push({ name: 'StockMappingPrintLocation' })
    },
    getProductCode (name) {
      const arrayName = name.split('-')
      return arrayName[arrayName.length - 1]
    },
    getMappingPhoto (mapping) {
      const photoUrls = mapping.product?.photoUrls || []
      return photoUrls.length ? this.$convertImgix(`${photoUrls[0]}?w=128`) : this.noPicture
    },
    async exportAllLocation () {
      try {
        this.loadingExport = true
        const { data } = await stockMappingService.exportAllLocation({ search: this.search })
        this.download(data)
      } catch (error) {
        console.error('exportAllLocation', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      } finally {
        this.loadingExport = false
      }
    },
    download (url, fileName) {
      let tmpFilename = fileName

      if (!fileName) {
        tmpFilename = this.$dayjs().format('YYYYMMDDHHmmss')
      }
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = tmpFilename
      downloadLink.click()
    }
  }
}
</script>

<style scoped>
img {
  max-height: 80px;
}
</style>
